import { useState } from 'react'
import { router, Link } from '@inertiajs/react'
import { AdminLayout } from '../../../layouts/admin-layout'
import { AppHeader } from '../../../components/app-header'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card'
import { Badge } from '../../../components/ui/badge'
import { Button } from '../../../components/ui/button'

interface AgentModel {
  id: string
  speed?: string
}

interface Agent {
  id: string
  name: string
  model: AgentModel | string
  version: number
  description: string | null
  system: string | null
  tools: Array<{ type: string; [key: string]: unknown }>
  mcp_servers: Array<{ name: string; type: string; url?: string }>
  skills: Array<{ type: string; name?: string }>
  metadata: Record<string, string>
  created_at: string
  updated_at: string
  archived_at: string | null
}

interface Session {
  id: string
  status: string
  agent: { id: string; version?: number } | string
  environment_id: string
  created_at: string
  updated_at: string
  archived_at: string | null
}

interface AgentVersion {
  version: number
  name: string
  model: AgentModel | string
  system: string | null
  updated_at: string
}

interface Props {
  agent: Agent
  sessions: Session[]
  versions: AgentVersion[]
  flash?: { success?: string; error?: string }
}

function modelId(model: Agent['model']): string {
  if (typeof model === 'string') return model
  return model?.id || 'unknown'
}

function formatDateTime(dateStr: string): string {
  return new Date(dateStr).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function sessionStatusVariant(status: string): 'default' | 'secondary' | 'destructive' {
  switch (status) {
    case 'running': return 'default'
    case 'terminated': return 'destructive'
    default: return 'secondary'
  }
}

function Show({ agent, sessions, versions }: Props) {
  const [archiving, setArchiving] = useState(false)
  const [sessionAction, setSessionAction] = useState<string | null>(null)
  const [showSystem, setShowSystem] = useState(false)
  const [showVersions, setShowVersions] = useState(false)

  const handleArchiveAgent = () => {
    if (!confirm(`Archive agent "${agent.name}"? New sessions cannot reference it.`)) return
    setArchiving(true)
    router.post(`/admin/managed_agents/${agent.id}/archive`, {}, {
      onFinish: () => setArchiving(false),
    })
  }

  const handleArchiveSession = (sessionId: string) => {
    if (!confirm('Archive this session? No new events can be sent.')) return
    setSessionAction(sessionId)
    router.post(`/admin/managed_agents/sessions/${sessionId}/archive`, {}, {
      onFinish: () => setSessionAction(null),
    })
  }

  const handleDeleteSession = (sessionId: string) => {
    if (!confirm('Permanently delete this session and its sandbox? This cannot be undone.')) return
    setSessionAction(sessionId)
    router.delete(`/admin/managed_agents/sessions/${sessionId}`, {
      onFinish: () => setSessionAction(null),
    })
  }

  return (
    <>
      <AppHeader breadcrumbs={[{ label: 'Managed Agents', href: '/admin/managed_agents' }, { label: agent.name }]} />

      <div className="p-6 space-y-6">
        <div className="flex items-center gap-2">
          <Link href="/admin/managed_agents">
            <Button variant="ghost" size="sm">← Back to Agents</Button>
          </Link>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Agent Info */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>{agent.name}</CardTitle>
                    <CardDescription className="font-mono text-xs mt-1">{agent.id}</CardDescription>
                  </div>
                  {agent.archived_at ? (
                    <Badge variant="secondary">Archived</Badge>
                  ) : (
                    <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">Active</Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Model</p>
                    <p className="font-mono">{modelId(agent.model)}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Version</p>
                    <p className="font-mono">v{agent.version}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Created</p>
                    <p>{formatDateTime(agent.created_at)}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Updated</p>
                    <p>{formatDateTime(agent.updated_at)}</p>
                  </div>
                  {agent.archived_at && (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Archived</p>
                      <p>{formatDateTime(agent.archived_at)}</p>
                    </div>
                  )}
                </div>

                {agent.description && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-1">Description</p>
                    <p className="text-sm">{agent.description}</p>
                  </div>
                )}

                {agent.system && (
                  <div>
                    <button
                      onClick={() => setShowSystem(!showSystem)}
                      className="text-sm font-medium text-muted-foreground hover:text-foreground"
                    >
                      System Prompt {showSystem ? '▾' : '▸'}
                    </button>
                    {showSystem && (
                      <pre className="mt-2 p-3 bg-muted rounded-lg text-xs overflow-x-auto max-h-64 overflow-y-auto whitespace-pre-wrap">
                        {agent.system}
                      </pre>
                    )}
                  </div>
                )}

                {agent.tools?.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-1">Tools ({agent.tools.length})</p>
                    <div className="flex flex-wrap gap-1">
                      {agent.tools.map((tool, i) => (
                        <Badge key={i} variant="outline" className="font-mono text-xs">{tool.type}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                {agent.mcp_servers?.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-1">MCP Servers ({agent.mcp_servers.length})</p>
                    <div className="flex flex-wrap gap-1">
                      {agent.mcp_servers.map((srv, i) => (
                        <Badge key={i} variant="outline" className="font-mono text-xs">{srv.name}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                {agent.skills?.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-1">Skills ({agent.skills.length})</p>
                    <div className="flex flex-wrap gap-1">
                      {agent.skills.map((skill, i) => (
                        <Badge key={i} variant="outline" className="font-mono text-xs">{skill.name || skill.type}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                {agent.metadata && Object.keys(agent.metadata).length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-1">Metadata</p>
                    <pre className="p-3 bg-muted rounded-lg text-xs overflow-x-auto">
                      {JSON.stringify(agent.metadata, null, 2)}
                    </pre>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Sessions */}
            <Card>
              <CardHeader>
                <CardTitle>Sessions ({sessions.length})</CardTitle>
                <CardDescription>Active and past sessions for this agent</CardDescription>
              </CardHeader>
              <CardContent>
                {sessions.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-4 text-center">No sessions found</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-border">
                          <th className="text-left px-3 py-2 font-medium">Session ID</th>
                          <th className="text-left px-3 py-2 font-medium">Status</th>
                          <th className="text-left px-3 py-2 font-medium">Created</th>
                          <th className="text-right px-3 py-2 font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sessions.map(session => (
                          <tr key={session.id} className="border-b border-border hover:bg-muted/30">
                            <td className="px-3 py-2 font-mono text-xs">{session.id}</td>
                            <td className="px-3 py-2">
                              <Badge variant={sessionStatusVariant(session.status)}>
                                {session.status}
                              </Badge>
                            </td>
                            <td className="px-3 py-2 text-muted-foreground">{formatDate(session.created_at)}</td>
                            <td className="px-3 py-2 text-right space-x-2">
                              {!session.archived_at && session.status !== 'terminated' && (
                                <button
                                  onClick={() => handleArchiveSession(session.id)}
                                  disabled={sessionAction === session.id}
                                  className="text-amber-600 hover:underline text-xs disabled:opacity-50"
                                >
                                  Archive
                                </button>
                              )}
                              {session.status !== 'running' && (
                                <button
                                  onClick={() => handleDeleteSession(session.id)}
                                  disabled={sessionAction === session.id}
                                  className="text-red-600 hover:underline text-xs disabled:opacity-50"
                                >
                                  Delete
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Actions */}
            <Card>
              <CardHeader>
                <CardTitle>Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {!agent.archived_at && (
                  <Button
                    variant="outline"
                    className="w-full text-amber-600 border-amber-300 hover:bg-amber-50 dark:hover:bg-amber-950"
                    onClick={handleArchiveAgent}
                    disabled={archiving}
                  >
                    {archiving ? 'Archiving...' : 'Archive Agent'}
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* Version History */}
            <Card>
              <CardHeader>
                <button
                  onClick={() => setShowVersions(!showVersions)}
                  className="flex items-center justify-between w-full"
                >
                  <CardTitle>Version History ({versions.length})</CardTitle>
                  <span className="text-muted-foreground">{showVersions ? '▾' : '▸'}</span>
                </button>
              </CardHeader>
              {showVersions && (
                <CardContent>
                  <div className="space-y-3">
                    {versions.map(v => (
                      <div key={v.version} className="flex items-center justify-between text-sm border-b border-border pb-2 last:border-0">
                        <div>
                          <span className="font-mono font-medium">v{v.version}</span>
                          <span className="text-muted-foreground ml-2">{modelId(v.model)}</span>
                        </div>
                        <span className="text-xs text-muted-foreground">{formatDate(v.updated_at)}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              )}
            </Card>
          </div>
        </div>
      </div>
    </>
  )
}

Show.layout = (page: React.ReactNode) => <AdminLayout>{page}</AdminLayout>

export default Show
